'use client';

import clsx from 'clsx';
import { PAGE_HERO_BAND_HOME_CLASS } from '@/constants/pageSections';
import { Heading1 } from '@/components/section/headings';
import { CubeSlider, type CubeCarouselSlide } from '@/components/widget';
import AppLink from '@/components/i18n/AppLink';
import { Span, Strong } from '@/components/ui';

export type HomeHeroTrustPoint = { value: string; label: string };

export type HomeTechHeroSectionProps = {
  kicker: string;
  title: string;
  subtitle: string;
  slides: CubeCarouselSlide[];
  primaryHref: string;
  primaryLabel: string;
  secondaryHref: string;
  secondaryLabel: string;
  trustPoints?: readonly HomeHeroTrustPoint[];
  className?: string;
};

/**
 * B2B IT / consulting hero: headline, dual CTAs, and the cube media carousel.
 */
const HomeTechHeroSection: React.FC<HomeTechHeroSectionProps> = ({
  kicker,
  title,
  subtitle,
  slides,
  primaryHref,
  primaryLabel,
  secondaryHref,
  secondaryLabel,
  trustPoints = [],
  className,
}) => {
  return (
    <section
      className={clsx(PAGE_HERO_BAND_HOME_CLASS, className)}
      aria-labelledby="home-hero-heading"
    >
      <div className="page-hero-split">
        <div className="flex flex-col justify-center text-center md:text-start gap-4">
          <Span className="text-xs font-semibold uppercase tracking-[0.2em] text-prose-muted">
            {kicker}
          </Span>
          <div id="home-hero-heading">
            <Heading1
              title={title}
              subtitle={subtitle}
              titleClassName="text-balance text-prose"
              subtitleClassName="mt-4 text-prose-muted max-w-xl mx-auto md:mx-0"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 pt-2">
            <AppLink href={primaryHref} btn="primary" className="shrink-0">
              {primaryLabel}
            </AppLink>
            <AppLink
              href={secondaryHref}
              className="text-sm font-medium text-prose-muted underline-offset-4 hover:text-prose hover:underline"
              title={secondaryLabel}
            >
              {secondaryLabel}
            </AppLink>
          </div>
          {trustPoints.length > 0 ? (
            <ul className="page-hero-trust m-0 list-none p-0" aria-label="Company highlights">
              {trustPoints.map((point) => (
                <li key={point.label} className="page-hero-trust__item">
                  <Strong className="text-base font-semibold tabular-nums text-prose xs:text-lg">{point.value}</Strong>
                  <Span className="text-xs text-prose-muted">{point.label}</Span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="page-hero-media">
          <CubeSlider slides={slides} />
        </div>
      </div>
    </section>
  );
};

export default HomeTechHeroSection;
